import { lazy, Suspense, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import { ReviewedBy } from "@/components/ReviewedBy";
import { DynamicDetailedLoanCard } from "@/components/DynamicDetailedLoanCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StickyCTA } from "@/components/StickyCTA";
import { useStickyCTA } from "@/hooks/useStickyCTA";
import {
  CheckCircle2,
  Shield,
  Sparkles,
  Home,
  Car,
  Layers,
  AlertCircle,
  Lightbulb,
  Calculator,
  XCircle,
  TrendingDown,
  ArrowRight,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RiskWarning } from "@/components/RiskWarning";
import { DynamicTrustpilotRating } from "@/components/DynamicTrustpilotRating";
import { LenderLiveDataStrip } from "@/components/LenderLiveDataStrip";
import { useProviderLiveTermsMap } from "@/hooks/useProviderLiveTerms";
import { CalculatorSkeleton } from "@/components/calculators/CalculatorSkeleton";
const PrivateLoanCalculator = lazy(() =>
  import("@/components/calculators/PrivateLoanCalculator").then((m) => ({ default: m.PrivateLoanCalculator })),
);
const Footer = lazy(() => import("@/components/Footer"));
import { useLanguage } from "@/contexts/LanguageContext";
import { ComparisonMethodology } from "@/components/content/ComparisonMethodology";
import { DecisionPathLinks } from "@/components/content/DecisionPathLinks";
import { generateComparisonPageSchema } from "@/utils/schemaGenerators";
import { advisaData } from "@/data/providers/advisa";
import { anyfinData } from "@/data/providers/anyfin";
import { enklareData } from "@/data/providers/enklare";
import { lanekoll2Data } from "@/data/providers/lanekoll2";
import { lendoData } from "@/data/providers/lendo";
import { plus1Data } from "@/data/providers/plus1";
import { samblaData } from "@/data/providers/sambla";
import { thornData } from "@/data/providers/thorn";
import { tryggaData } from "@/data/providers/trygga";
import { zensumData } from "@/data/providers/zensum";
import {
  applyLiveTermsToComparisonItems,
  getLatestLiveTermsUpdatedAt,
  getLiveAmountLabel,
  getLiveTermsVerifiedLabel,
  getPreferredRateLabel,
} from "@/utils/providerLiveTerms";
import type { ProviderData } from "@/types/provider";
// Hardcoded Sambla logo for immediate render
import samblaLogo from "@/assets/sambla_stor.png";

const privateLoanProviders: ProviderData[] = [
  samblaData,
  lanekoll2Data,
  tryggaData,
  lendoData,
  enklareData,
  advisaData,
  zensumData,
  thornData,
  plus1Data,
  anyfinData,
];

const privateLoanLiveSlugs = [
  "sambla",
  "lanekoll2",
  "trygga",
  "lendo",
  "enklare",
  "advisa",
  "zensum",
  "thorn",
  "plus1",
  "anyfin",
];

const privateLoanSchemaItems = [
  {
    name: "Sambla privatlån",
    provider: "Sambla",
    url: "/sv-SE/privatlan/sambla",
    description: "Låneförmedlare som jämför privatlån från många långivare med en ansökan.",
    amount: "5 000 - 800 000 kr",
    interestRate: "Från 5,08 % effektiv ränta",
    term: "1 - 20 år",
    creditCheck: "UC",
  },
  {
    name: "Lendo privatlån",
    provider: "Lendo",
    url: "/sv-SE/privatlan/lendo",
    description: "Jämför flera privatlåneerbjudanden med en ansökan.",
    amount: "10 000 - 800 000 kr",
    interestRate: "Individuell ränta",
    term: "1 - 15 år",
    creditCheck: "UC",
  },
  {
    name: "Enklare privatlån",
    provider: "Enklare",
    url: "/sv-SE/privatlan/enklare",
    description: "Låneförmedlare för privatlån och samlingslån.",
    amount: "5 000 - 800 000 kr",
    interestRate: "Individuell ränta",
    term: "1 - 20 år",
    creditCheck: "UC",
  },
  {
    name: "Advisa privatlån",
    provider: "Advisa",
    url: "/sv-SE/privatlan/advisa",
    description: "Jämför privatlån och samlingslån från flera långivare.",
    amount: "5 000 - 800 000 kr",
    interestRate: "Individuell ränta",
    term: "1 - 20 år",
    creditCheck: "UC",
  },
  {
    name: "Lånekoll privatlån",
    provider: "Lånekoll",
    url: "/sv-SE/privatlan/lanekoll2",
    description: "Jämförelse av privatlån med digital ansökan.",
    amount: "5 000 - 600 000 kr",
    interestRate: "Individuell ränta",
    term: "1 - 15 år",
    creditCheck: "UC",
  },
];

const privateLoanSchemaBase = {
  name: "Jämför privatlån från flera långivare",
  description: "Klarfinans jämför privatlån, låneförmedlare och enskilda långivare med fokus på effektiv ränta, belopp, löptid och villkor.",
  url: "/sv-SE/privatlan",
  category: "Privatlån",
  breadcrumbs: [
    { name: "Start", url: "https://klarfinans.se" },
    { name: "Privatlån" },
  ],
};

const PrivateLoanPage = () => {
  const { currentLanguage } = useLanguage();
  const { data: liveTermsByProvider = {} } = useProviderLiveTermsMap("provider", privateLoanLiveSlugs);
  const samblaLiveTerms = liveTermsByProvider.sambla ?? null;
  const samblaVerifiedLabel = getLiveTermsVerifiedLabel(samblaLiveTerms);
  const showStickyCTA = useStickyCTA();
  const privateLoanSchemas = useMemo(
    () => generateComparisonPageSchema({
      ...privateLoanSchemaBase,
      items: applyLiveTermsToComparisonItems(privateLoanSchemaItems, liveTermsByProvider),
      updatedAt: getLatestLiveTermsUpdatedAt(liveTermsByProvider),
    }),
    [liveTermsByProvider]
  );

  // Handle hash scroll (e.g. from navigation CTA button)
  useEffect(() => {
    if (window.location.hash === '#comparison-section') {
      const timeoutId = setTimeout(() => {
        const element = document.getElementById('comparison-section');
        if (element) {
          const navHeight = 80; // Account for fixed navigation
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementPosition - navHeight,
            behavior: 'smooth'
          });
          // Clear hash after scroll to prevent re-triggering
          window.history.replaceState(null, '', window.location.pathname);
        }
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, []);
  return (
    <div className="min-h-screen">
      <SEO 
        title="Privatlån - Jämför privatlån från 40+ långivare"
        description="Jämför privatlån från över 40 långivare. Låna upp till 800 000 kr med konkurrenskraftiga räntor. Svar inom 24h och smidig digital process."
        keywords={["privatlån", "blancolån", "jämför privatlån", "lån utan säkerhet", "låneförmedlare", "lån"]}
        schemaData={privateLoanSchemas}
      />

      <main>
        <section className="relative overflow-hidden text-white pt-20 pb-8 md:pt-24 md:pb-12 lg:pt-28 lg:pb-16" style={{ backgroundColor: "hsl(var(--primary-dark))" }}>
          <div className="container mx-auto px-3 md:px-4 relative z-10">
            <div className="grid gap-6 md:gap-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start lg:gap-14">
              <div className="space-y-4 md:space-y-5 lg:space-y-6">
                <Badge variant="outline" className="border-white/25 bg-white/10 text-white">
                  <Sparkles className="mr-2 h-3.5 w-3.5" />
                  Handplockade långivare
                </Badge>
                <h1 className="max-w-3xl text-3xl md:text-5xl lg:text-6xl lg:leading-[1.05] lg:tracking-tight font-bold leading-tight text-balance">
                  Jämför privatlån utan krångel
                </h1>
                <p className="max-w-2xl text-base md:text-lg leading-7 text-white/90">
                  Vi listar inte alla – vi har handplockat de bästa låneförmedlarna och långivarna åt dig. Jämför belopp, ränta och villkor innan du går vidare.
                </p>

                <div className="grid max-w-2xl grid-cols-3 divide-x divide-white/20 rounded-lg border border-white/20 bg-white/10">
                  {[
                    ["Gratis", "att jämföra"],
                    ["800 000 kr", "maxbelopp"],
                    ["24h", "vanligt svar"],
                  ].map(([value, label]) => (
                    <div key={label} className="px-3 py-3">
                      <p className="whitespace-nowrap text-base font-bold leading-tight text-white sm:text-lg">{value}</p>
                      <p className="mt-1 text-xs text-white/75">{label}</p>
                    </div>
                  ))}
                </div>

                <div className="hero-cta-section pt-2 max-w-sm">
                  <Button
                    asChild
                    size="lg"
                    className="w-full bg-white text-slate-950 hover:bg-white/90 shadow-none text-sm sm:text-base px-4"
                  >
                    <a href="#comparison-section">
                      Se våra utvalda långivare
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>

              <div className="max-w-lg mx-auto lg:mx-0">
                <div className="space-y-3 md:space-y-4">
                  <div className="text-center mb-6 md:mb-4">
                    <h2 className="text-lg md:text-xl font-bold text-white">Låneförmedlare i fokus</h2>
                    <p className="text-white/90 text-sm font-medium">{samblaVerifiedLabel}</p>
                  </div>

                  <Card className="p-4 md:p-5 space-y-3 md:space-y-4 bg-card border border-primary/20 shadow-lg">
                    <span className="inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                      Jämför 40 långivare
                    </span>

                    <div className="space-y-3">
                      <div className="flex justify-center items-center">
                        <img
                          src={samblaLogo}
                          alt="Sambla"
                          className="h-10 sm:h-12 md:h-14 w-auto object-contain max-w-full"
                        />
                      </div>
                      <DynamicTrustpilotRating
                        entityType="provider"
                        entitySlug="sambla"
                        fallbackRating={4.8}
                        fallbackReviewCount={29456}
                        size="md"
                        showScore={true}
                        showLogo={true}
                      />
                      <LenderLiveDataStrip
                        entityType="provider"
                        entitySlug="sambla"
                        className="justify-center"
                        liveTermsOverride={samblaLiveTerms}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3 md:gap-4 py-3 md:py-4 border-y border-border">
                      <div>
                        <p className="text-xs md:text-sm text-muted-foreground">Effektiv ränta</p>
                        <p className="font-bold text-primary text-2xl md:text-3xl">
                          {getPreferredRateLabel(samblaLiveTerms) || "från 5,08%"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs md:text-sm text-muted-foreground">Lånebelopp</p>
                        <p className="text-sm font-semibold text-foreground mt-1">
                          {getLiveAmountLabel(samblaLiveTerms, "5 000 - 800 000 kr")}
                        </p>
                      </div>
                    </div>

                    <ul className="space-y-2">
                      {[
                        "En ansökan och en UC-kontroll",
                        "Svar samma dag från flera långivare",
                        "Kostnadsfri jämförelse",
                      ].map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="space-y-3">
                      <Button asChild size="lg" className="w-full bg-primary hover:bg-primary/90 text-white">
                        <a href="https://go.adt284.net/t/t?a=1923302872&as=2024680921&t=2&tk=1" target="_blank" rel="sponsored nofollow noopener noreferrer">
                          Ansök hos Sambla
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>

                      <Button
                        asChild
                        size="lg"
                        variant="outline"
                        className="w-full border border-muted-foreground/30 text-foreground bg-transparent hover:bg-transparent hover:text-foreground hover:border-muted-foreground/50 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 group"
                      >
                        <Link to={`/${currentLanguage}/privatlan/sambla`} className="no-underline">
                          Läs mer om Sambla
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </div>

                    <RiskWarning />
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Innehållsrik informationssektion */}
        <section className="py-8 md:py-12 bg-background">
          <div className="container max-w-4xl">
            {/* Vad är ett privatlån? */}
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Vad är ett privatlån?</h2>

              <div className="prose prose-lg max-w-none mb-8 text-muted-foreground">
                <p className="mb-4">
                  Ett privatlån, även kallat blancolån, är ett lån utan säkerhet som du kan använda till i princip vad
                  du vill. Till skillnad från ett{" "}
                  <Link to={`/${currentLanguage}/bolan`} className="text-primary hover:underline font-medium">
                    bolån
                  </Link>{" "}
                  är det alltså inte knutet till ett visst objekt. Detta innebär att banken inte har någon pant eller
                  säkerhet för lånet, vilket resulterar i en högre ränta än lån med säkerhet.
                </p>
                <p>
                  Många banker och långivare erbjuder privatlån på mellan 5 000 och 600 000 kronor, men det finns även
                  långivare som erbjuder upp till 800 000 kronor. Återbetalningstiden ligger vanligtvis på mellan 2 och
                  15 år, där vissa långivare erbjuder löptider på upp till 20 år.
                </p>
              </div>

              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-4">Viktiga fakta om privatlån:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <strong>Inget säkerhetskrav:</strong> Till skillnad från bolån behöver du inte pantsätta något
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <strong>Flexibel användning:</strong> Pengarna kan användas till vad du vill
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <strong>Högre ränta:</strong> Eftersom banken tar större risk blir räntan högre än för bolån
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <strong>Kreditprövning:</strong> Banken bedömer din återbetalningsförmåga innan godkännande
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Loan Providers Section */}
        <section id="comparison-section" className="pt-4 md:pt-6 lg:pt-8 pb-8 md:pb-12 lg:pb-16 bg-background">
          <div className="container pl-safe-left pr-safe-right max-w-6xl mx-auto">
            <div className="mb-6 max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">Vårt urval</p>
              <h2 className="mt-1 text-2xl md:text-3xl font-bold">Handplockade långivare och låneförmedlare</h2>
              <p className="mt-2 text-sm md:text-base text-muted-foreground">
                Vi presenterar inte alla på marknaden – bara de vi själva skulle välja. Jämför belopp, effektiv ränta, löptid och villkor. Räntan sätts alltid individuellt efter kreditprövning.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {privateLoanProviders.slice(1).map((provider) => {
                if (!provider) return null;

                // Hjälpfunktion för att hitta värde från termsAndConditions
                const getTermValue = (term: string): string => {
                  const item = provider.termsAndConditions?.find((t) => t.term === term);
                  return item?.value || "N/A";
                };

                // Bestäm lenderBadge baserat på provider
                let lenderBadge = "";
                if (provider.slug === "lanekoll2") {
                  lenderBadge = "Jämför 20 långivare";
                } else if (provider.slug === "enklare") {
                  lenderBadge = "Jämför 40 långivare";
                } else if (provider.slug === "trygga") {
                  lenderBadge = "Jämför 32 långivare";
                } else if (provider.slug === "lendo") {
                  lenderBadge = "Jämför 40 långivare";
                } else if (provider.slug === "advisa") {
                  lenderBadge = "Jämför 40 långivare";
                } else if (provider.slug === "zensum") {
                  lenderBadge = "Jämför 20 långivare";
                } else if (provider.slug === "thorn" || provider.slug === "plus1" || provider.slug === "anyfin") {
                  lenderBadge = "Enskild långivare";
                }

                // Hämta värden från provider-data
                const loanAmount = getTermValue("Lånebelopp");
                const nominalRate = getTermValue("Nominell ränta");
                const effectiveRate = getTermValue("Effektiv ränta");
                const loanTerm = getTermValue("Löptid");
                const loanWithoutUC = getTermValue("Lån utan UC");
                const responseTime = getTermValue("Svar på ansökan");

                // Betalningslösning ska alltid vara "Banköverföring"
                const paymentSolution = "Banköverföring";

                const link = `/${currentLanguage}/privatlan/${provider.slug}`;

                return (
                  <div
                    key={provider.slug}
                    className="w-full lg:max-w-sm"
                  >
                    <DynamicDetailedLoanCard
                      provider={provider.name}
                      logo={provider.logo}
                      loanAmount={loanAmount}
                      nominalRate={nominalRate}
                      effectiveRate={effectiveRate}
                      loanTerm={loanTerm}
                      loanWithoutUC={loanWithoutUC}
                      responseTime={responseTime}
                      paymentSolution={paymentSolution}
                      link={link}
                      lenderBadge={lenderBadge}
                      entityType="provider"
                      entitySlug={provider.slug}
                      fallbackRating={provider.trustpilot?.rating || 4.0}
                      fallbackReviewCount={provider.trustpilot?.reviews || 0}
                      liveTermsOverride={liveTermsByProvider[provider.slug] ?? null}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <ComparisonMethodology
          title="Så jämför vi privatlån"
          description="Privatlån ska jämföras på faktisk kostnad, krav och återbetalningsförmåga. Den lägsta räntan i marknadsföringen är inte samma sak som den ränta användaren får."
          items={[
            {
              title: "Effektiv ränta väger tungt",
              body: "Vi lyfter effektiv ränta eftersom den inkluderar både ränta och avgifter. Den individuella räntan sätts alltid efter kreditprövning.",
            },
            {
              title: "Förmedlare och långivare skiljs åt",
              body: "En låneförmedlare jämför flera långivare. En enskild långivare ger bara sitt eget erbjudande. Det ska synas tydligt i listan.",
            },
            {
              title: "Samlingslån får egna interna länkar",
              body: "Besökare med flera krediter ska snabbt kunna läsa om samlingslån, refinansiering och effektiv ränta innan de ansöker.",
            },
          ]}
        />

        <DecisionPathLinks
          title="Gå rätt vidare efter privatlånejämförelsen"
          description="De flesta besökare behöver antingen jämföra långivare, räkna på månadskostnad eller förstå ett begrepp innan de ansöker. Här samlas de viktigaste nästa stegen."
          links={[
            {
              title: "Jämför långivare",
              description: "Hoppa direkt till listan med privatlån, låneförmedlare och långivare.",
              href: "#comparison-section",
              label: "Till jämförelsen",
              badge: "Populärast",
            },
            {
              title: "Räkna på lånet",
              description: "Se ungefärlig månadskostnad innan du skickar en ansökan.",
              href: "#loan-calculator",
              label: "Öppna kalkylatorn",
            },
            {
              title: "Förstå effektiv ränta",
              description: "Lär dig varför effektiv ränta är bättre än nominell ränta när lån jämförs.",
              href: `/${currentLanguage}/guider/effektiv-ranta`,
              label: "Läs guiden",
            },
            {
              title: "Samla flera lån",
              description: "Se när ett samlingslån kan sänka kostnaden för dyra krediter.",
              href: `/${currentLanguage}/guider/samlingslån`,
              label: "Läs om samlingslån",
            },
            {
              title: "Lån utan säkerhet",
              description: "Få en tydlig förklaring av blancolån och vanliga krav.",
              href: `/${currentLanguage}/guider/lan-utan-sakerhet`,
              label: "Läs guiden",
            },
            {
              title: "Mindre lån utan UC",
              description: "Jämför alternativ där kreditupplysningen inte påverkar UC.",
              href: `/${currentLanguage}/lana-utan-uc`,
              label: "Jämför lån utan UC",
            },
          ]}
        />

        {/* Innehållsrik informationssektion - fortsättning */}
        <section className="py-8 md:py-12 bg-background">
          <div className="container max-w-4xl">
            {/* Jämför privatlån */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold mb-6">Jämför privatlån för att hitta det bästa erbjudandet</h2>

              <div className="prose prose-lg max-w-none mb-8 text-muted-foreground">
                <p className="mb-4">
                  För att hitta ett privatlån med så låg ränta som möjligt bör du jämföra erbjudanden hos flera olika
                  långivare. Med hjälp av tabellen högst upp på den här sidan kan du snabbt se vilka långivare som
                  erbjuder de bästa villkoren just nu.
                </p>
                <p>
                  Vi visar den nominella räntan hos varje långivare, men i räkneexemplet anger vi även den{" "}
                  <Link
                    to={`/${currentLanguage}/guider/effektiv-ranta`}
                    className="text-primary hover:underline font-medium"
                  >
                    effektiva räntan
                  </Link>
                  . Det är den effektiva räntan du främst ska fokusera på eftersom den inkluderar både räntekostnader
                  och andra avgifter som avi- och uppläggningsavgifter.
                </p>
              </div>

              <Alert className="flex items-start gap-3">
                <AlertDescription className="flex-1">
                  <strong>Viktigt att veta:</strong> Det finns inga garantier för att du får exakt den effektiva ränta
                  som långivaren presenterar. Långivare sätter alltid räntan utifrån sin bedömning av din
                  kreditvärdighet och dina ekonomiska förutsättningar.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </section>

        {/* Användningsområden */}
        <section className="bg-muted/30 py-8 md:py-12">
          <div className="container max-w-4xl">
            <h2 className="text-3xl font-bold mb-6">Det här kan du använda ett privatlån till</h2>

            <div className="prose prose-lg max-w-none mb-6 text-muted-foreground">
              <p>
                Du kan använda ett privatlån till i princip vad du vill inom konsumtion. Här är några vanliga
                användningsområden:
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2 flex items-center gap-2">
                    <Home className="h-5 w-5 text-primary" />
                    Renovering
                  </h3>
                  <p className="text-sm text-muted-foreground">Byt ut vitvaror, renovera kök eller badrum</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2 flex items-center gap-2">
                    <Car className="h-5 w-5 text-primary" />
                    Fordon
                  </h3>
                  <p className="text-sm text-muted-foreground">Köp bil, motorcykel eller andra fordon</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2 flex items-center gap-2">
                    <Layers className="h-5 w-5 text-primary" />
                    Samlingslån
                  </h3>
                  <p className="text-sm text-muted-foreground">Samla flera lån och krediter i ett enda lån</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Möbler
                  </h3>
                  <p className="text-sm text-muted-foreground">Inred hemmet eller byt ut gamla möbler</p>
                </CardContent>
              </Card>
            </div>

            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                Många väljer också att ta ett så kallat{" "}
                <Link
                  to={`/${currentLanguage}/guider/samlingslån`}
                  className="text-primary hover:underline font-medium"
                >
                  samlingslån
                </Link>
                . Det är egentligen ett vanligt privatlån, men som du använder för att betala av mindre skulder. Genom
                att samla dina lån kan du sänka dina räntekostnader och således dina månadskostnader. Räntan på
                privatlån må vara högre än på lån med säkerhet, men den är i alla fall oftast lägre än på många smålån.
              </p>
            </div>
          </div>
        </section>

        {/* Kontantinsatslån */}
        <section className="py-8 md:py-12 bg-background">
          <div className="container max-w-4xl">
            <div className="mb-8">
              <h2 className="text-3xl font-bold mb-6">Kan man ta privatlån till kontantinsatsen?</h2>

              <div className="prose prose-lg max-w-none mb-6 text-muted-foreground">
                <p>
                  Ja, det finns möjlighet att ansöka om ett privatlån för att finansiera hela eller en del av{" "}
                  <Link
                    to={`/${currentLanguage}/guider/kontantinsats-och-bolanetak`}
                    className="text-primary hover:underline font-medium"
                  >
                    kontantinsatsen
                  </Link>{" "}
                  när du tar bolån. Det kallas kort och gott kontantinsatslån, men det är alltså ett helt vanligt
                  privatlån.
                </p>
              </div>

              <Alert className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Tänk på!</strong> Det är inte alltid som banken accepterar att man lånar till kontantinsatsen,
                  så kolla med din bank vad som gäller i ditt fall.
                </AlertDescription>
              </Alert>

              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p className="mb-4">
                  Lånar du pengar både till kontantinsatsen och själva bostaden måste du betala både ränta och{" "}
                  <Link
                    to={`/${currentLanguage}/guider/amortera-eller-spara`}
                    className="text-primary hover:underline font-medium"
                  >
                    amortering
                  </Link>{" "}
                  på två lån under en längre tid framöver, så se noga över din ekonomi innan du ansöker om ett
                  kontantinsatslån. Särskilt med tanke på att räntan troligtvis kommer att vara högre än på själva
                  bostadslånet.
                </p>
                <p>
                  Ett kontantinsatslån kan absolut vara en bra lösning för att kunna köpa en bostad, men det bästa är
                  alltid om du kan spara ihop pengarna till kontantinsatsen själv.
                </p>
              </div>
            </div>

            {/* Krav för att få privatlån */}
            <div className="mb-12 md:mb-16">
              <h2 className="text-3xl font-bold mb-6">Vad krävs för att få privatlån?</h2>

              <Card>
                <CardContent className="pt-6">
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <strong>Ålder:</strong> Du ska vara över 18 år (vissa långivare kräver 20 år)
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <strong>Identitet:</strong> Du ska vara folkbokförd i Sverige och ha svenskt personnummer
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <strong>Inkomst:</strong> Du måste ha en viss stabil inkomst
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <div>
                        <strong>Ekonomi:</strong> Du måste vara skuldfri hos Kronofogden
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Privatlån utan UC */}
        <section className="bg-muted/30 py-8 md:py-12">
          <div className="container max-w-4xl">
            <h2 className="text-3xl font-bold mb-6">Finns det privatlån utan UC?</h2>

            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-4">
                Ja, det finns långivare som erbjuder privatlån och använder ett annat{" "}
                <Link
                  to={`/${currentLanguage}/guider/kreditupplysning`}
                  className="text-primary hover:underline font-medium"
                >
                  kreditupplysningsföretag
                </Link>{" "}
                än UC, men de är ytterst få. UC är fortfarande Sveriges största kreditupplysningsföretag och används av
                de flesta banker och långivare, i alla fall när det gäller större privatlån och bolån.
              </p>
              <p>
                Långivare som använder kreditupplysningsföretag som Creditsafe eller Dun & Bradstreet erbjuder i regel
                mindre lån, som snabblån och kontokrediter. Värt att nämna är att dessa typer av lån ofta har betydligt
                högre räntor än "vanliga" privatlån och även kortare återbetalningstider.
              </p>
            </div>
          </div>
        </section>

        {/* Private Loan Calculator */}
        <section id="loan-calculator" className="bg-muted/30 py-8 md:py-12">
          <div className="container max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Beräkna din månadskostnad</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Använd vår kalkylator för att få en uppskattning av vad ditt privatlån kommer att kosta per månad.
              </p>
            </div>
            <Suspense fallback={<CalculatorSkeleton variant="detailed" />}>
              <PrivateLoanCalculator defaultAmount={100000} />
            </Suspense>
          </div>
        </section>

        {/* Amorteringsformer */}
        <section className="py-8 md:py-12 bg-background">
          <div className="container max-w-4xl">
            <div className="mb-12 md:mb-16">
              <h2 className="text-3xl font-bold mb-6">Har privatlån rak amortering eller annuitet?</h2>

              <div className="prose prose-lg max-w-none mb-8 text-muted-foreground">
                <p>
                  Du kan betala av privatlån både med rak{" "}
                  <Link
                    to={`/${currentLanguage}/guider/amortera-eller-spara`}
                    className="text-primary hover:underline font-medium"
                  >
                    amortering
                  </Link>{" "}
                  och{" "}
                  <Link
                    to={`/${currentLanguage}/guider/annuitetslån`}
                    className="text-primary hover:underline font-medium"
                  >
                    annuitet
                  </Link>
                  , och vilket alternativ som gäller just för ditt lån ska framgå i villkoren. Du kan inte välja
                  återbetalningsmetod själv utan långivarna tillämpar helt enkelt olika metoder.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                      <TrendingDown className="h-5 w-5 text-primary" />
                      Annuitet (vanligast)
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Du betalar en fast summa varje månad som inkluderar både ränta och amortering.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
                        <span>Fast månadskostnad hela lånetiden</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
                        <span>Lägre kostnad i början</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                        <span>Högre totalkostnad över tid</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                      <Calculator className="h-5 w-5 text-primary" />
                      Rak amortering
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Amorteringssumman är densamma varje månad, men totalkostnaden minskar över tid.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
                        <span>Lägre totalkostnad över tid</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
                        <span>Snabbare skuld-minskning</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                        <span>Högre kostnad i början</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-4">
                <Alert>
                  <Lightbulb className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Tips!</strong> Rak amortering har oftast billigare totalkostnad än annuitet, men med
                    annuitet slipper du betala mer i början av återbetalningsperioden.
                  </AlertDescription>
                </Alert>

                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Bra att veta:</strong> Om du tar ett lån och sedan ångrar dig har du 14 dagars ångerrätt
                    enligt lag. Detta ger dig tid att omvärdera ditt beslut.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Vanliga frågor om privatlån</h2>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-left">Hur fungerar ett privatlån?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p>
                    Ett privatlån (även kallat{" "}
                    <Link
                      to={`/${currentLanguage}/guider/lan-utan-sakerhet`}
                      className="text-primary hover:underline font-medium"
                    >
                      blancolån
                    </Link>
                    ) är ett{" "}
                    <Link
                      to={`/${currentLanguage}/guider/lan-utan-sakerhet`}
                      className="text-primary hover:underline font-medium"
                    >
                      lån utan säkerhet
                    </Link>
                    , vilket betyder att du inte behöver pantsätta bostad, bil eller annan egendom. Du betalar tillbaka
                    lånet månadsvis med ränta och amortering under en bestämd tid.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger className="text-left">Hur mycket kan jag låna med ett privatlån?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p className="mb-4">
                    Beloppet du kan låna beror på din inkomst, befintliga skulder och{" "}
                    <Link
                      to={`/${currentLanguage}/guider/kreditupplysning`}
                      className="text-primary hover:underline font-medium"
                    >
                      kreditvärdighet
                    </Link>
                    . Vanligtvis kan du låna mellan 10 000 och 600 000 kronor, men villkoren varierar mellan långivare.
                  </p>
                  <p>
                    Läs mer om{" "}
                    <Link
                      to={`/${currentLanguage}/guider/hur-mycket-kan-jag-lana`}
                      className="text-primary hover:underline font-medium"
                    >
                      hur mycket du kan låna
                    </Link>
                    .
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger className="text-left">Vad påverkar räntan på privatlån?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p className="mb-4">Räntan sätts individuellt och baseras på flera faktorer:</p>
                  <ul className="list-disc pl-6 space-y-2 mb-4">
                    <li>
                      Din{" "}
                      <Link
                        to={`/${currentLanguage}/guider/kreditupplysning`}
                        className="text-primary hover:underline font-medium"
                      >
                        kreditvärdighet
                      </Link>
                    </li>
                    <li>Hur mycket du lånar</li>
                    <li>Återbetalningstid</li>
                    <li>Om du ansöker ensam eller med en medlåntagare (vilket ofta ger lägre ränta)</li>
                  </ul>
                  <p>
                    Det är viktigt att jämföra{" "}
                    <Link
                      to={`/${currentLanguage}/guider/effektiv-ranta`}
                      className="text-primary hover:underline font-medium"
                    >
                      effektiv ränta
                    </Link>{" "}
                    mellan olika långivare.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger className="text-left">Vad kan jag använda ett privatlån till?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p className="mb-4">
                    Du kan använda ett privatlån till nästan vad som helst – till exempel renovering, bilköp, samla dyra
                    krediter eller finansiera en resa. Eftersom lånet saknar säkerhet bestämmer du själv ändamålet.
                  </p>
                  <p>
                    Många använder privatlån för att göra ett{" "}
                    <Link
                      to={`/${currentLanguage}/guider/samlingslån`}
                      className="text-primary hover:underline font-medium"
                    >
                      samlingslån
                    </Link>{" "}
                    eller för{" "}
                    <Link
                      to={`/${currentLanguage}/guider/refinansiering`}
                      className="text-primary hover:underline font-medium"
                    >
                      refinansiering
                    </Link>{" "}
                    av befintliga skulder.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger className="text-left">
                  Vad är skillnaden mellan blancolån och privatlån?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p>
                    <Link
                      to={`/${currentLanguage}/guider/lan-utan-sakerhet`}
                      className="text-primary hover:underline font-medium"
                    >
                      Blancolån
                    </Link>{" "}
                    och privatlån är i praktiken samma sak. Båda är lån utan säkerhet som privatpersoner kan använda
                    fritt, och räntan beror på{" "}
                    <Link
                      to={`/${currentLanguage}/guider/kreditupplysning`}
                      className="text-primary hover:underline font-medium"
                    >
                      kreditvärdighet
                    </Link>
                    .
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger className="text-left">Kan jag lösa mitt privatlån i förtid?</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p>
                    Många långivare tillåter förtidsinlösen, men vissa kan ta ut en avgift för det. Att lösa lånet i
                    förtid kan spara ränta, så det kan vara lönsamt att undersöka villkoren innan du tar lånet.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7">
                <AccordionTrigger className="text-left">
                  Vad händer om jag inte kan betala tillbaka privatlånet i tid?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p>
                    Om du missar betalningar kan långivaren ta ut förseningsavgifter, och din skuld kan skickas till
                    inkasso. Det påverkar också din{" "}
                    <Link
                      to={`/${currentLanguage}/guider/kreditupplysning`}
                      className="text-primary hover:underline font-medium"
                    >
                      kreditvärdighet
                    </Link>{" "}
                    negativt, vilket kan göra det svårare att få lån i framtiden.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <ReviewedBy variant="default" />

        {/* CTA Section */}
        <section className="py-8 md:py-12 bg-background">
          <div className="container max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Redo att jämföra privatlån?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Hitta det bästa privatlånet för dig. Jämför räntor, villkor och få svar snabbt.
            </p>
            <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90">
              <a href="#comparison-section">
                Jämför privatlån
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </section>

      </main>
      
      <Suspense fallback={null}>
        <Footer />
      </Suspense>

      <StickyCTA show={showStickyCTA}>
        <div className="container mx-auto max-w-5xl px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">Sambla</p>
              <p className="truncate text-xs text-muted-foreground">Jämför 40 långivare — kostnadsfritt</p>
            </div>
            <Button asChild size="lg" className="whitespace-nowrap bg-primary text-white hover:bg-primary/90">
              <a
                href="https://go.adt284.net/t/t?a=1923302872&as=2024680921&t=2&tk=1"
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
              >
                Ansök hos Sambla
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </StickyCTA>
    </div>
  );
};
export default PrivateLoanPage;
